<script setup lang="ts">
    /**
     * Implements software requirements: H1SR-11
     *
     * @link https://formuslabs.youtrack.cloud/issue/H1SR-11/Default-values-for-cases-can-be-defined-in-the-hip-preferences
     */
    import { ROUTES } from '@/router';
    import SurgicalGuidePreferences from '@/views/preferences/components/SurgicalGuidePreferences.vue';
    import StemSystemSelection from '@/components/stems/StemSystemSelection.vue';
    import { usePreferencesViewModel } from '@/views/preferences/usePreferencesViewModel';
    import HipSpinePreferences from '@/views/preferences/hipSpine/HipSpinePreferences.vue';
    import { PREFERENCES_TARGETS } from '@/domain/analytics/analyticsTargets';
    import {
        MAX_ANTEVERSION,
        MAX_INCLINATION,
        MIN_ANTEVERSION,
        MIN_INCLINATION,
    } from '@/domain/anatomy/pelvis/anteversionInclination';

    const {
        state,
        hasError,
        message,
        hasUnsavedChanges,
        isSaving,
        isReady,
        preferencesLoadFailed,
        showSurgicalGuide,
        showHipSpine,
        checkFieldError,
        alignmentModeOptions,
        stemSystemGroups,
        selectedStemTypesBySystem,
        stemSystems,
        updateSelectedStems,
        surgicalApproachId,
        surgicalApproachOptions,
        surgicalGuideHasUnsavedChanges,
        saveSurgicalGuide,
        updateSurgicalApproach,
        surgeonOptions,
        selectedSurgeonId,
        isSubjectSelected,
        hasSurgeonOptions,
        isEditingOwnPreferences,
        isSubjectReady,
        subjectLoadFailed,
        selectSurgeon,
        save,
        loadSurgeons,
    } = usePreferencesViewModel();

    const inclinationOutOfRangeMessage = `Please provide a number between ${MIN_INCLINATION} and ${MAX_INCLINATION}`;
    const anteversionOutOfRangeMessage = `Please provide a number between ${MIN_ANTEVERSION} and ${MAX_ANTEVERSION}`;

    // Only the surgeon list is awaited, so the page paints as soon as it knows its subject.
    // Preferences load on selection, so switching surgeon never re-enters Suspense.
    await loadSurgeons();
</script>

<template>
    <v-layout>
        <app-sidebar>
            <router-link :to="{ name: ROUTES.HOME }">
                <app-link icon="mdi-arrow-left">Back to your Cases</app-link>
            </router-link>
        </app-sidebar>

        <app-main-view>
            <app-form-title data-testid="preferences-page-title">
                Surgical Preferences
            </app-form-title>
            <app-form-subtitle data-testid="preferences-page-subtitle">
                These preferences will be applied to each new Formus Case created for this surgeon.
                They can also be overridden per-Case after it has been created, and before it has
                been processed.
            </app-form-subtitle>

            <app-main-card v-if="!isEditingOwnPreferences">
                <app-notification
                    v-if="subjectLoadFailed"
                    data-testid="preferences-surgeons-error"
                    variant="error"
                    title="We could not load your surgeons"
                    message="Please refresh and try again, or contact the Formus support team via the help option at the top right of the page."
                />
                <app-notification
                    v-else-if="isSubjectReady && !hasSurgeonOptions"
                    data-testid="preferences-no-surgeons"
                    variant="error"
                    title="No surgeons available"
                    message="You are not linked to any surgeon, so there are no preferences for you to edit. Please contact your organisation administrator."
                />
                <v-alert
                    v-else-if="hasSurgeonOptions && !isSubjectSelected"
                    data-testid="preferences-select-a-surgeon"
                    type="info"
                    variant="tonal"
                    density="comfortable"
                    class="mb-10"
                >
                    Choose a surgeon below to view and edit their surgical preferences.
                </v-alert>

                <template v-if="hasSurgeonOptions">
                    <app-form-section-title>Surgeon</app-form-section-title>
                    <app-form-group
                        label="Surgeon"
                        :show-label="false"
                        data-testid="preferences-surgeon-control"
                    >
                        <v-select
                            variant="outlined"
                            density="compact"
                            label="Select a surgeon"
                            input-id="preferences-surgeon-choices"
                            :model-value="selectedSurgeonId"
                            :items="
                                surgeonOptions.map(({ name, id }) => ({ title: name, value: id }))
                            "
                            data-testid="preferences-surgeon-select"
                            @update:model-value="selectSurgeon"
                        />
                    </app-form-group>
                </template>
            </app-main-card>

            <app-main-card v-if="isSubjectSelected && preferencesLoadFailed">
                <app-notification
                    data-testid="preferences-load-error"
                    variant="error"
                    title="We could not load these surgical preferences"
                    message="Please refresh and try again, or contact the Formus support team via the help option at the top right of the page."
                />
            </app-main-card>

            <app-main-card v-if="isSubjectSelected && isReady && !preferencesLoadFailed">
                <!--
                 There are 3 different error messages:
                    1. Error: When the inputs are invalid: Please review your input...
                    2. Error: When the application fails for some reason: 'Failed to save my surgical preferences'
                    3. Success: 'Surgical Preferences were successfully updated!'

                    The code below make the test pass but it will need to be addressed in the future.
                    e.g.: At the moment the form is able to be submitted even if the inputs are invalid.
                    Once this is fixed, the test will need to be updated and the v-if condition below
                    will need to be changed and will be simpler
                 -->
                <app-notification
                    v-if="hasError && !message"
                    data-testid="preferences-notification"
                    variant="error"
                    title="Please review your input"
                    message="Your changes were not saved. Please review the errors marked in red below and save again."
                />
                <app-notification
                    v-else-if="message"
                    data-testid="preferences-notification"
                    :title="message"
                    :variant="hasError ? 'error' : 'success'"
                />

                <app-form-section-title>Stem</app-form-section-title>
                <app-form-group
                    label="Preferred System"
                    :show-label="false"
                    data-testid="preferred-system"
                >
                    <stem-system-selection
                        :stem-system-groups="stemSystemGroups"
                        :selected-stem-types-by-system="selectedStemTypesBySystem"
                        :preferred-system="state.preferredSystem"
                        :stem-systems="stemSystems"
                        :show-preferred-system-error="checkFieldError('stems')"
                        @update:preferred-system="state.preferredSystem = $event"
                        @update:selected-stems="updateSelectedStems"
                    />
                    <p
                        v-if="checkFieldError('preferredSystem')"
                        class="text-error text-caption mt-2"
                    >
                        Please select an option
                    </p>
                </app-form-group>

                <app-form-section-title>Cup</app-form-section-title>

                <app-form-group
                    label="Inclination Angle"
                    data-testid="inclination-angle-control"
                    input-id="inclination-angle-input"
                    :data-analytics-id="PREFERENCES_TARGETS.cupInclinationAngle"
                >
                    <app-text-input
                        v-model="state.cup.inclinationAngle"
                        suffix="deg"
                        input-id="inclination-angle-input"
                        :error-messages="
                            checkFieldError('cupInclinationAngle')
                                ? inclinationOutOfRangeMessage
                                : ''
                        "
                    />
                </app-form-group>

                <app-form-group label="Fitting mode" data-testid="fitting-mode-buttons">
                    <app-toggle
                        v-model="state.cup.fitMethod"
                        :error-messages="
                            checkFieldError('cupFittingMode') ? 'Please select an option' : ''
                        "
                        :options="[
                            { label: 'Auto', value: 'acid' },
                            { label: 'Fossa', value: 'fossa' },
                        ]"
                    />
                </app-form-group>

                <app-form-group label="Anteversion mode" data-testid="anteversion-angle-buttons">
                    <app-toggle
                        v-model="state.cup.anteversion"
                        :error-messages="
                            checkFieldError('cupAnteversionMode') ? 'Please select an option' : ''
                        "
                        :options="[
                            { label: 'Combined', value: 'auto' },
                            { label: 'Manual', value: 'manual' },
                        ]"
                    />
                </app-form-group>

                <app-form-group
                    v-if="state.cup.anteversion === 'manual'"
                    label="Anteversion Angle"
                    data-testid="anteversion-angle-input"
                    input-id="anteversion-angle-input"
                    class="mt-12"
                >
                    <app-text-input
                        v-model="state.cup.anteversionAngle"
                        suffix="deg"
                        input-id="anteversion-angle-input"
                        :error-messages="
                            checkFieldError('cupAnteversionAngle')
                                ? anteversionOutOfRangeMessage
                                : ''
                        "
                    />
                </app-form-group>

                <app-form-group label="Alignment mode" data-testid="alignment-mode-buttons">
                    <app-toggle
                        v-model="state.cup.alignment"
                        :error-messages="
                            checkFieldError('alignmentMode') ? 'Please select an option' : ''
                        "
                        :options="alignmentModeOptions"
                    />
                </app-form-group>
                <app-form-actions>
                    <app-form-action-notification
                        v-if="hasUnsavedChanges"
                        data-testid="form-has-changes-warning"
                        message="You have unsaved changes on this page"
                    />
                    <app-button-primary
                        height="55"
                        class="px-8"
                        width="350"
                        data-testid="preferences-submit-button"
                        :loading="isSaving"
                        :disabled="!hasUnsavedChanges"
                        @click="save"
                        >Save Surgical Preferences
                    </app-button-primary>
                </app-form-actions>
            </app-main-card>

            <app-main-card v-if="isSubjectSelected && showHipSpine">
                <HipSpinePreferences />
            </app-main-card>

            <app-main-card
                v-if="isSubjectSelected && showSurgicalGuide"
                :data-analytics-id="PREFERENCES_TARGETS.surgicalGuideCard"
            >
                <SurgicalGuidePreferences
                    :surgical-approach-id="surgicalApproachId"
                    :surgical-approach-options="surgicalApproachOptions"
                    :has-unsaved-changes="surgicalGuideHasUnsavedChanges"
                    @save="saveSurgicalGuide"
                    @update:surgical-approach="updateSurgicalApproach"
                />
            </app-main-card>
        </app-main-view>
    </v-layout>
</template>

<style scoped>
    :deep(.v-messages) {
        font-size: 14px;
        margin-top: 10px;
    }
</style>
